import type { ParameterValue } from 'models/FeatureParameter'

/**
 * URL codec for typed parameter values (AB#4461).
 *
 * Vue Router serialises a string[] as repeated query keys and omits an empty array
 * entirely, while the legacy URL carried one JSON-string value. This codec is the only
 * place that knows the URL shape, so consumers keep typed values in the live store.
 *
 * Encoding (the explicit empty-array encoding):
 * - Array (including empty)   -> JSON text, e.g. `["a","b"]` or `[]`; never omitted.
 * - Object                    -> JSON text.
 * - Strings and numbers       -> plain text; null and undefined are not written.
 *
 * Decoding accepts the legacy shapes too: a repeated-key array from Vue Router and a
 * JSON-array or JSON-object string from an old shared URL.
 */

/** Encode a typed value for a URL query value. Returns undefined when nothing should be written. */
export const encodeParameterUrlValue = (value: ParameterValue | undefined): string | undefined => {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}

/** Result of decoding JSON-looking text: the parsed value, or the original text when it is not valid JSON. */
interface DecodedText {
  value: ParameterValue
  malformed: boolean
}

/** Decode persisted legacy JSON text once at the parameter-store boundary. */
export const decodeLegacyParameterValue = (value: ParameterValue): ParameterValue => {
  if (typeof value !== 'string') return value
  return decodeText(value).value
}

/** Decode JSON-looking text; plain text and malformed JSON both come back as a string. */
const decodeText = (raw: string): DecodedText => {
  const trimmed = raw.trim()
  const looksJson = trimmed.startsWith('[') || trimmed.startsWith('{')
  if (!looksJson) return { value: raw, malformed: false }
  try {
    return { value: JSON.parse(trimmed) as ParameterValue, malformed: false }
  } catch {
    return { value: raw, malformed: true }
  }
}

/**
 * Decode a URL query value back to a typed value.
 * - A repeated-key array from Vue Router becomes that string array.
 * - A JSON array or object string is parsed; plain text stays a string.
 */
export const decodeParameterUrlValue = (
  raw: string | string[] | null | undefined
): ParameterValue | undefined => {
  if (raw === undefined || raw === null) return undefined
  if (Array.isArray(raw)) return raw.filter((v): v is string => typeof v === 'string')
  return decodeText(raw).value
}

/**
 * Read a typed parameter value as scalar text for consumers that need a string
 * (date and single-select reads). Arrays and objects are not scalar text, so they yield undefined.
 */
export const parameterScalarText = (value: ParameterValue | undefined): string | undefined => {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return `${value}`
  return undefined
}

/**
 * Read a multi-select value as a typed array. Native arrays pass through; a legacy
 * JSON-string array is decoded at this boundary. Empty, missing or non-array values yield [].
 */
export const toParameterValueArray = (value: ParameterValue | undefined): ParameterValue[] => {
  if (Array.isArray(value)) return value
  if (typeof value !== 'string' || value.length === 0) return []
  const decoded = decodeText(value).value
  return Array.isArray(decoded) ? decoded : []
}

/** Whether a legacy multi-select string resembles an array but cannot be decoded. */
export const isMalformedLegacyParameterArray = (value: ParameterValue | undefined): boolean => {
  if (typeof value !== 'string' || !value.trim().startsWith('[')) return false
  return decodeText(value).malformed
}

/** Resolve a selected twin ID from typed values or a legacy JSON string array. */
export const parameterTwinId = (value: ParameterValue | undefined): string | undefined => {
  const candidates = toParameterValueArray(value)
  if (candidates.length > 0) return typeof candidates[0] === 'string' ? candidates[0] : undefined
  return typeof value === 'string' ? value : undefined
}

/** Read a multi-select value as string IDs for consumers that explicitly require IDs. */
export const toStringArray = (value: ParameterValue | undefined): string[] =>
  toParameterValueArray(value).map((v) => `${v}`)

/**
 * The string the parameter store holds for a URL value (B1: the store is still string-typed).
 * Arrays round-trip as JSON text, including the explicit empty array `[]`.
 */
export const urlValueToStoreString = (raw: string | string[] | null | undefined): string =>
  encodeParameterUrlValue(decodeParameterUrlValue(raw)) ?? ''
